"use client";

import React, { useState } from "react";
import { CheckCircle2, AlertTriangle, XCircle, RefreshCcw } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

const QUESTIONS = [
  "Do you have more than 50 unread bookmarks across social platforms?",
  "Do you save content thinking 'I'll need this' but never revisit it?",
  "Is your YouTube 'Watch Later' list longer than 100 videos?",
  "Do you struggle to find a specific post you saved last month?",
  "Does looking at your 'Saved' folders make you feel overwhelmed?",
];

export const HoardingQuiz = () => {
  const [answers, setAnswers] = useState<boolean[]>([]);
  const [showResult, setShowResult] = useState(false);

  const handleAnswer = (answer: boolean) => {
    const newAnswers = [...answers, answer];
    if (newAnswers.length === QUESTIONS.length) {
      setAnswers(newAnswers);
      setShowResult(true);
    } else {
      setAnswers(newAnswers);
    }
  };

  const resetQuiz = () => {
    setAnswers([]);
    setShowResult(false);
  };

  const score = answers.filter(a => a).length;
  const currentQuestionIndex = answers.length;

  return (
    <div className="my-12 p-8 rounded-[2.5rem] bg-surface-solid border border-border-light relative overflow-hidden shadow-2xl">
      <div className="absolute top-0 right-0 w-64 h-64 bg-brand-1/5 rounded-full blur-[100px] -mr-32 -mt-32" />
      
      <div className="relative z-10">
        <div className="text-center mb-10">
          <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-brand-1/10 border border-brand-1/20 text-brand-1 text-[10px] font-black uppercase tracking-widest mb-4">
            Interactive Assessment
          </span>
          <h3 className="text-3xl font-black text-white tracking-tight">
            Are You a Digital Hoarder?
          </h3>
        </div>

        <AnimatePresence mode="wait">
          {!showResult ? (
            <motion.div
              key="question"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              className="space-y-8"
            >
              <div className="text-center">
                <p className="text-xl text-text-primary font-medium mb-8 leading-relaxed">
                  {QUESTIONS[currentQuestionIndex]}
                </p>
                <div className="flex justify-center gap-4">
                  <button
                    onClick={() => handleAnswer(true)}
                    className="px-10 py-4 rounded-2xl bg-white text-black font-black hover:bg-brand-1 hover:text-white transition-all shadow-xl active:scale-95"
                  >
                    YES
                  </button>
                  <button
                    onClick={() => handleAnswer(false)}
                    className="px-10 py-4 rounded-2xl bg-white/5 border border-white/10 text-white font-black hover:bg-white/10 transition-all active:scale-95"
                  >
                    NO
                  </button>
                </div>
              </div>
              <div className="flex justify-center gap-2">
                {QUESTIONS.map((_, i) => (
                  <div
                    key={i}
                    className={`w-2 h-2 rounded-full transition-all duration-500 ${
                      i === currentQuestionIndex 
                        ? "w-8 bg-brand-1" 
                        : i < currentQuestionIndex ? "bg-brand-1/40" : "bg-white/10"
                    }`}
                  />
                ))}
              </div>
            </motion.div>
          ) : (
            <motion.div
              key="result"
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              className="text-center space-y-6"
            >
              <div className="flex justify-center">
                {score >= 4 ? (
                  <XCircle className="w-16 h-16 text-red-500" />
                ) : score >= 2 ? (
                  <AlertTriangle className="w-16 h-16 text-yellow-500" />
                ) : (
                  <CheckCircle2 className="w-16 h-16 text-green-500" />
                )}
              </div>
              
              <div>
                <h4 className="text-2xl font-bold text-white mb-2">
                  {score >= 4 ? "Critical Hoarding Detected" : score >= 2 ? "The Early Stages" : "Pro Curator Mindset"}
                </h4>
                <p className="text-text-secondary leading-relaxed max-w-md mx-auto">
                  {score >= 4 
                    ? "Your digital library has become a graveyard. It's time to stop hoarding and start building a Second Brain." 
                    : score >= 2 
                    ? "You're saving valuable things, but without a system, they'll be lost forever. You need better curation tools."
                    : "You have a clean system, but could you be even more efficient with AI?"}
                </p>
              </div>

              <div className="pt-4 flex flex-col items-center gap-4">
                <button
                  onClick={resetQuiz}
                  className="flex items-center gap-2 text-text-muted hover:text-white transition-colors text-xs font-bold uppercase tracking-widest"
                >
                  <RefreshCcw className="w-3 h-3" /> Retake Quiz
                </button>
                
                <div className="w-full h-px bg-white/5 my-4" />
                
                <p className="text-sm font-bold text-white uppercase tracking-widest mb-2">Your Cure:</p>
                <a
                  href="/sign-in"
                  className="inline-flex items-center gap-2 px-8 py-4 bg-brand-1 text-white font-black rounded-2xl shadow-[0_0_30px_rgba(91,91,255,0.4)] hover:scale-105 transition-all uppercase tracking-widest text-sm"
                >
                  Start Curating with Tavlo
                </a>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};

export default HoardingQuiz;
